// Newsletter template rendering — converts articles to email-safe HTML
// Three layout styles based on article position:
//   Position 0:   Featured (full-width hero image, large headline)
//   Position 1-2: Two-column (side-by-side cards)
//   Position 3+:  Compact (thumbnail + text list)

export interface ArticleForRender {
  title: string
  excerpt: string | null
  cover_image_url: string | null
  author: string | null
  permalink: string | null
  categories: string[]
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function renderFeatured(a: ArticleForRender): string {
  const cat = a.categories[0]
    ? `<p style="font-family:Georgia,serif;font-size:11px;text-transform:uppercase;letter-spacing:2px;color:#b91c1c;margin:0 0 8px;">${esc(a.categories[0])}</p>`
    : ''
  const img = a.cover_image_url
    ? `<img src="${esc(a.cover_image_url)}" alt="${esc(a.title)}" width="560" style="width:100%;height:auto;max-height:280px;object-fit:cover;border-radius:4px;display:block;" />`
    : ''
  const author = a.author ? `<span style="color:#6b7280;">By ${esc(a.author)}</span>` : ''
  const excerpt = a.excerpt
    ? `<p style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:15px;line-height:1.6;color:#374151;margin:0 0 16px;">${esc(a.excerpt)}</p>`
    : ''
  const cta = a.permalink
    ? `<a href="${esc(a.permalink)}" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;font-weight:600;color:#1d4ed8;text-decoration:none;">READ THE FULL STORY &rarr;</a>`
    : ''

  return `<tr><td style="padding:24px 20px;">
  ${img ? `<div style="margin-bottom:16px;">${img}</div>` : ''}
  ${cat}
  <h2 style="font-family:Georgia,serif;font-size:26px;font-weight:700;color:#1a1a1a;margin:0 0 8px;line-height:1.2;">${esc(a.title)}</h2>
  <p style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;margin:0 0 12px;">${author}</p>
  ${excerpt}
  ${cta}
</td></tr>`
}

export function renderTwoColumnPair(a1: ArticleForRender, a2: ArticleForRender | null): string {
  const renderCard = (a: ArticleForRender): string => {
    const cat = a.categories[0]
      ? `<p style="font-family:Georgia,serif;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#b91c1c;margin:0 0 6px;">${esc(a.categories[0])}</p>`
      : ''
    const img = a.cover_image_url
      ? `<img src="${esc(a.cover_image_url)}" alt="${esc(a.title)}" width="260" style="width:100%;height:auto;max-height:160px;object-fit:cover;border-radius:4px;display:block;margin-bottom:12px;" />`
      : ''
    const author = a.author
      ? `<p style="font-size:12px;color:#6b7280;margin:0 0 8px;">By ${esc(a.author)}</p>`
      : ''
    const excerpt = a.excerpt
      ? `<p style="font-size:13px;line-height:1.5;color:#374151;margin:0 0 10px;">${esc(a.excerpt.slice(0, 120))}${a.excerpt.length > 120 ? '...' : ''}</p>`
      : ''
    const cta = a.permalink
      ? `<a href="${esc(a.permalink)}" style="font-size:13px;font-weight:600;color:#1d4ed8;text-decoration:none;">READ MORE &rarr;</a>`
      : ''

    return `<td width="48%" valign="top" style="padding:0;">
  ${img}${cat}
  <h3 style="font-family:Georgia,serif;font-size:17px;font-weight:700;color:#1a1a1a;margin:0 0 6px;line-height:1.3;">${esc(a.title)}</h3>
  ${author}${excerpt}${cta}
</td>`
  }

  const col2 = a2 ? renderCard(a2) : '<td width="48%"></td>'

  return `<tr><td style="padding:20px;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
    ${renderCard(a1)}
    <td width="4%"></td>
    ${col2}
  </tr></table>
</td></tr>`
}

export function renderCompact(a: ArticleForRender): string {
  const img = a.cover_image_url
    ? `<td width="80" valign="top" style="padding-right:14px;"><img src="${esc(a.cover_image_url)}" alt="" width="80" height="80" style="width:80px;height:80px;object-fit:cover;border-radius:4px;display:block;" /></td>`
    : ''
  const cat = a.categories[0]
    ? `<span style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#b91c1c;">${esc(a.categories[0])}</span> &middot; `
    : ''
  const author = a.author ? `${esc(a.author)}` : ''
  const excerpt = a.excerpt
    ? `<p style="font-size:13px;line-height:1.5;color:#374151;margin:0 0 6px;">${esc(a.excerpt.slice(0, 100))}${a.excerpt.length > 100 ? '...' : ''}</p>`
    : ''
  const cta = a.permalink
    ? `<a href="${esc(a.permalink)}" style="font-size:12px;font-weight:600;color:#1d4ed8;text-decoration:none;">READ MORE &rarr;</a>`
    : ''

  return `<tr><td style="padding:14px 20px;border-bottom:1px solid #e5e7eb;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
    ${img}
    <td valign="top">
      <h4 style="font-family:Georgia,serif;font-size:15px;font-weight:700;color:#1a1a1a;margin:0 0 4px;line-height:1.3;">${esc(a.title)}</h4>
      <p style="font-size:11px;color:#6b7280;margin:0 0 6px;">${cat}${author}</p>
      ${excerpt}${cta}
    </td>
  </tr></table>
</td></tr>`
}

export function renderAdBanner(slot: 1 | 2): string {
  if (slot === 1) {
    return `<tr><td style="padding:16px 20px;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#0f172a;border-radius:6px;overflow:hidden;">
    <tr><td style="padding:20px 24px;">
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
        <td valign="middle">
          <p style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#fbbf24;margin:0 0 6px;">PARTNER OFFER</p>
          <p style="font-family:Georgia,serif;font-size:18px;font-weight:700;color:#ffffff;margin:0 0 6px;">Fly SAS Business — 30% off with code MAXPTS</p>
          <p style="font-size:13px;color:#94a3b8;margin:0;">Stockholm → New York from 24,900 SEK. Book by April 15.</p>
        </td>
        <td width="120" valign="middle" style="text-align:center;">
          <a href="#" style="display:inline-block;background:#fbbf24;color:#0f172a;font-size:13px;font-weight:700;padding:10px 18px;border-radius:4px;text-decoration:none;">BOOK NOW</a>
        </td>
      </tr></table>
    </td></tr>
  </table>
</td></tr>`
  }

  return `<tr><td style="padding:16px 20px;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
    <td width="55%" valign="top" style="padding-right:16px;">
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:linear-gradient(135deg,#1e3a5f,#2563eb);background-color:#1e3a5f;border-radius:6px;">
        <tr><td style="padding:18px 20px;">
          <p style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#93c5fd;margin:0 0 8px;">SPONSORED</p>
          <p style="font-family:Georgia,serif;font-size:16px;font-weight:700;color:#ffffff;margin:0 0 8px;line-height:1.3;">Chase Sapphire Preferred — 80,000 bonus points</p>
          <p style="font-size:12px;color:#bfdbfe;margin:0 0 10px;">Worth $1,000+ in travel. $95 annual fee.</p>
          <a href="#" style="font-size:12px;font-weight:600;color:#fbbf24;text-decoration:none;">Apply now &rarr;</a>
        </td></tr>
      </table>
    </td>
    <td width="45%" valign="top" style="background:#fef3c7;border-radius:6px;padding:18px 20px;">
      <p style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#92400e;margin:0 0 8px;">THIS WEEK IN NUMBERS</p>
      <p style="font-family:Georgia,serif;font-size:32px;font-weight:700;color:#1a1a1a;margin:0 0 4px;">47%</p>
      <p style="font-size:13px;color:#374151;margin:0;">of business class award seats go empty. Our guide shows you how to grab them.</p>
    </td>
  </tr></table>
</td></tr>`
}

export function renderFullNewsletter(
  articles: ArticleForRender[],
  newsletterName: string,
  editionDate: string,
): string {
  const masthead = `<tr><td style="padding:28px 20px 20px;text-align:center;border-bottom:2px solid #1a1a1a;">
  <h1 style="font-family:Georgia,serif;font-size:28px;font-weight:700;color:#1a1a1a;margin:0 0 4px;letter-spacing:1px;">${esc(newsletterName)}</h1>
  <p style="font-family:Georgia,serif;font-size:12px;color:#6b7280;margin:0;letter-spacing:0.5px;">${esc(editionDate)}</p>
</td></tr>`

  const divider = `<tr><td style="padding:0 20px;"><hr style="border:none;border-top:1px solid #d1d5db;margin:0;" /></td></tr>`

  let body = ''

  // Article 1: Featured
  if (articles[0]) body += renderFeatured(articles[0])
  body += divider

  // Ad slot 1 — SAS Business offer
  body += renderAdBanner(1)
  body += divider

  // Articles 2-3: Two-column
  if (articles[1]) body += renderTwoColumnPair(articles[1], articles[2] ?? null)
  if (articles[1]) body += divider

  // Ad slot 2 — Chase Sapphire + stat callout
  if (articles.length > 3) {
    body += renderAdBanner(2)
    body += divider
  }

  // Articles 4+: Compact list
  for (let i = 3; i < articles.length; i++) {
    const article = articles[i]
    if (!article) continue
    body += renderCompact(article)
  }

  const footer = `<tr><td style="padding:24px 20px;text-align:center;border-top:2px solid #1a1a1a;">
  <p style="font-family:Georgia,serif;font-size:14px;font-weight:700;color:#1a1a1a;margin:0 0 6px;">${esc(newsletterName)}</p>
  <p style="font-size:12px;color:#6b7280;margin:0 0 12px;">You're receiving this because you subscribed.</p>
  <p style="font-size:12px;margin:0;">
    <a href="{{unsubscribe_url}}" style="color:#6b7280;text-decoration:underline;">Unsubscribe</a>
  </p>
  <p style="font-size:11px;color:#9ca3af;margin:8px 0 0;">&copy; 2026 ${esc(newsletterName)}. All rights reserved.</p>
</td></tr>`

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<title>${esc(newsletterName)}</title></head>
<body style="margin:0;padding:0;background-color:#f8f7f4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
<center>
<table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color:#f8f7f4;">
<tr><td align="center" style="padding:20px 10px;">
<table width="600" cellpadding="0" cellspacing="0" role="presentation" style="background-color:#ffffff;border:1px solid #e5e7eb;border-radius:4px;max-width:600px;width:100%;">
${masthead}
${body}
${footer}
</table>
</td></tr></table>
</center>
</body></html>`
}
